// frontend/src/pages/admin/index.jsx
// Updated: April 8, 2025
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAdminLeads, getAgents, getTransactions } from '../../api';

// Simple loading spinner
const Spinner = () => (
  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
);

// Metric card component
const MetricCard = ({ title, value, icon, trend, link }) => (
  <Link to={link} className="block">
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {trend && (
            <p className={`text-sm mt-2 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last month
            </p>
          )}
        </div>
        <div className="text-blue-600 text-2xl">{icon}</div>
      </div>
    </div>
  </Link>
);

// Quick action button
const QuickAction = ({ title, icon, link, color = 'blue' }) => (
  <Link to={link} className={`inline-flex items-center px-4 py-2 rounded-md text-white bg-${color}-600 hover:bg-${color}-700 transition-colors`}>
    <span className="mr-2">{icon}</span>
    {title}
  </Link>
);

const AdminDashboardPage = () => {
  const [metrics, setMetrics] = useState({
    leads: { count: 0, trend: 0 },
    agents: { count: 0, trend: 0 },
    transactions: { count: 0, trend: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all data in parallel
        const [leadsResult, agentsResult, transactionsResult] = await Promise.all([
          getAdminLeads(),
          getAgents(),
          getTransactions()
        ]);

        // Update metrics
        setMetrics({
          leads: {
            count: leadsResult.success ? leadsResult.data.length : 0,
            trend: 12 // TODO: Calculate actual trend
          },
          agents: {
            count: agentsResult.success ? agentsResult.data.length : 0,
            trend: 5
          },
          transactions: {
            count: transactionsResult.success ? transactionsResult.data.length : 0,
            trend: 8
          }
        });

        // Set recent leads
        if (leadsResult.success) {
          setRecentLeads(leadsResult.data.slice(0, 5));
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 p-4 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>
        
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Total Leads"
            value={metrics.leads.count}
            icon="📊"
            trend={metrics.leads.trend}
            link="/admin/leads"
          />
          <MetricCard
            title="Active Agents"
            value={metrics.agents.count}
            icon="👥"
            trend={metrics.agents.trend}
            link="/admin/agents"
          />
          <MetricCard
            title="Total Transactions"
            value={metrics.transactions.count}
            icon="💰"
            trend={metrics.transactions.trend}
            link="/admin/transactions"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <QuickAction
              title="View Leads"
              icon="📋"
              link="/admin/leads"
              color="blue"
            />
            <QuickAction
              title="Manage Agents"
              icon="👥"
              link="/admin/agents"
              color="green"
            />
            <QuickAction
              title="View Transactions"
              icon="💰"
              link="/admin/transactions"
              color="purple"
            />
            <QuickAction
              title="Settings"
              icon="⚙️"
              link="/admin/settings"
              color="gray"
            />
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Leads</h2>
          {recentLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lead.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.state}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          lead.status === 'available' ? 'bg-green-100 text-green-800' :
                          lead.status === 'purchased' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Link to={`/admin/leads/${lead.id}`} className="text-blue-600 hover:text-blue-900">
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No recent leads found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;